import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Linkedin } from 'lucide-react'
import { useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import { useRequestDisconnectMutation } from '../hooks/use-request-disconnect-mutation'

// Self-contained disconnect control: a destructive button that opens a confirm
// dialog and, on confirm, removes the founder's LinkedIn sender from HeyReach
// and resets outreach to DISCONNECTED, dropping the founder back to the setup
// flow (so this component typically unmounts after success). The `inline`
// variant is a low-key text trigger for the setup panel's "connected" note.
export function DisconnectLinkedInButton({
  startupId,
  variant = 'button',
}: {
  startupId: string
  variant?: 'button' | 'inline' | 'secondary'
}) {
  const [open, setOpen] = useState(false)
  const disconnect = useRequestDisconnectMutation(startupId)

  return (
    <>
      {variant === 'inline' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-red-400 hover:underline"
        >
          Disconnect
        </button>
      ) : (
        <SecondaryButton
          size="sm"
          tone={variant === 'secondary' ? 'neutral' : 'danger'}
          icon={<Linkedin />}
          onClick={() => setOpen(true)}
        >
          Disconnect LinkedIn
        </SecondaryButton>
      )}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) disconnect.reset()
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect LinkedIn?</DialogTitle>
            <DialogDescription>
              This stops your campaign and disconnects your LinkedIn from our outreach system right
              away. You can set everything up again afterwards.
            </DialogDescription>
          </DialogHeader>
          {disconnect.isError && (
            <p className="text-[13px] text-red-400">Couldn't disconnect. Please try again.</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={disconnect.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                disconnect.mutate(undefined, {
                  onSuccess: () => {
                    trackEvent('outreach_linkedin_disconnect_requested', { startup_id: startupId })
                    setOpen(false)
                  },
                })
              }
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
